/*
 * WR-024 B — the Horizon page: a project's week-based timeline.
 *
 * The page owns the data and every mutation; HorizonTimeline is presentational
 * and the three editors (picker, lanes, milestone) only hand back a body. Every
 * scheduling mutation answers the full Horizon: `show()` takes a Horizon
 * from the server and makes it the current one.
 *
 * `show()` is also where the client and the server are compared. The view
 * recomputes the plan with the same scheduler the API uses, so the two must
 * agree; a disagreement is a bug in one of them and is logged rather than
 * silently drawn.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { flushSync } from 'react-dom';
import {
  ArrowLeft, CalendarRange, FileText, LayoutGrid, Layers, Diamond, CalendarCheck, Link2, Plus, Printer, Trash2, X,
} from 'lucide-react';
import UserAvatar from './UserAvatar';
import HorizonTimeline from './HorizonTimeline';
import HorizonPicker from './HorizonPicker';
import HorizonLanes from './HorizonLanes';
import HorizonMilestoneDialog from './HorizonMilestoneDialog';
import HorizonDeleteDialog from './HorizonDeleteDialog';
import HorizonPrint from './HorizonPrint';
import TaskModal from './TaskModal';
import { applyOrder, scheduleMismatches } from './horizonPlan';
import { formatAnchor, parseDay } from './horizonTime';
import { mediaUrl } from '../media';
import './HorizonPage.css';

const DAY = 86400000;

/** The Monday of this date's week, in UTC, as 'YYYY-MM-DD' — a Horizon's week 1. */
function mondayOf(date) {
  const day = parseDay(date);
  const dow = day.getUTCDay();                       // 0 = Sunday … 6 = Saturday
  return new Date(day.getTime() - ((dow + 6) % 7) * DAY).toISOString().slice(0, 10);
}

/** The server's message for a failed request, falling back to the transport's. */
const errorOf = (e) => e?.response?.data?.error || e?.message || 'Something went wrong';

function HorizonPage() {
  const { projectId, horizonId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [project, setProject] = useState(null);
  const [list, setList] = useState(null);        // the project's Horizons, most recent first
  const [horizon, setHorizon] = useState(null);
  const [error, setError] = useState(null);

  const [zoom, setZoom] = useState('week');
  const [links, setLinks] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', anchor: '', weeks: '26' });
  const [formError, setFormError] = useState(null);
  const [creatingBusy, setCreatingBusy] = useState(false);
  const createBusy = useRef(false);
  const createDialog = useRef(null);
  const createName = useRef(null);
  const createTrigger = useRef(null);

  useEffect(() => {
    if (!creating) return undefined;
    createName.current?.focus();
    const keyboard = (event) => {
      if (event.key === 'Escape' && !createBusy.current) {
        event.preventDefault();
        setCreating(false);
      }
      if (event.key !== 'Tab') return;
      const controls = Array.from(createDialog.current?.querySelectorAll('button:not([disabled]), input:not([disabled])') || []);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && (document.activeElement === first || !createDialog.current.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !createDialog.current.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', keyboard);
    return () => {
      document.removeEventListener('keydown', keyboard);
      if (createTrigger.current?.isConnected) createTrigger.current.focus();
    };
  }, [creating]);

  const [toast, setToast] = useState(null);
  const [pickerTasks, setPickerTasks] = useState(null);
  const [lanesOpen, setLanesOpen] = useState(false);
  const [milestone, setMilestone] = useState(undefined);   // undefined = closed, null = new
  const [task, setTask] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const closeDelete = useCallback(() => setDeleteTarget(null), []);
  const loadRequest = useRef(null);
  const activeHorizonId = useRef(horizonId);
  const activeProjectId = useRef(projectId);
  const mounted = useRef(true);
  activeHorizonId.current = horizonId;
  activeProjectId.current = projectId;
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const [reordering, setReordering] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [paper, setPaper] = useState('A3');

  const params = new URLSearchParams(location.search);
  const todayParam = params.get('today');
  const rowParam = params.get('row');
  const taskParam = params.get('task');

  /* The clock. Production always uses the real one; a dev build honours
     ?today=YYYY-MM-DD so the overdue state can be driven in a test. */
  const now = useMemo(() => (
    (process.env.NODE_ENV !== 'production' && parseDay(todayParam))
      ? new Date(`${todayParam}T12:00:00Z`)
      : new Date()
  ), [todayParam]);

  useEffect(() => { if (rowParam) setSelectedId(rowParam); }, [rowParam]);

  useEffect(() => {
    // Native Ctrl+P and the toolbar both prepare a separate, expanded Month
    // grid. flushSync makes it available before the browser snapshots print.
    const prepare = () => flushSync(() => setPrinting(true));
    const finish = () => setPrinting(false);
    window.addEventListener('beforeprint', prepare);
    window.addEventListener('afterprint', finish);
    return () => {
      window.removeEventListener('beforeprint', prepare);
      window.removeEventListener('afterprint', finish);
    };
  }, []);

  /** Take a Horizon from the server, after checking it against the client scheduler. */
  const show = useCallback((h) => {
    if (String(h._id) !== String(activeHorizonId.current)) return h;
    const mismatches = scheduleMismatches(h);
    if (mismatches.length) {
      console.error('Horizon schedule mismatch (client vs server)', mismatches);
    }
    setHorizon(h);
    setError(null);
    return h;
  }, []);

  const load = useCallback(async () => {
    if (!horizonId) return;
    const request = {};
    loadRequest.current = request;
    try {
      const res = await axios.get(`/horizons/${horizonId}`);
      if (request !== loadRequest.current) return;
      if (String(res.data.project) !== String(projectId)) {
        setError('This Horizon does not belong to this project.');
        return;
      }
      show(res.data);
    } catch (e) {
      if (request === loadRequest.current) setError(e?.response?.status === 404 ? 'This Horizon was not found.' : errorOf(e));
    }
  }, [horizonId, projectId, show]);

  // The project and its Horizons. Without an id in the path, open the most
  // recently updated one (the list is sorted) and keep the query string.
  useEffect(() => {
    let live = true;
    setProject(null);
    setList(null);
    setError(null);
    (async () => {
      try {
        const [pRes, lRes] = await Promise.all([
          axios.get(`/projects/${projectId}`),
          axios.get(`/horizons/project/${projectId}`),
        ]);
        if (!live) return;
        setProject(pRes.data);
        setList(lRes.data || []);
        if (!horizonId && (lRes.data || []).length > 0) {
          navigate(`/project/${projectId}/horizon/${lRes.data[0]._id}${location.search}`, { replace: true });
        }
      } catch (e) {
        if (live) setError(errorOf(e));
      }
    })();
    return () => { live = false; };
  }, [projectId]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setHorizon(null);
    setSelectedId(rowParam || null);
    setTask(null);
    setReordering(false);
    setPickerTasks(null);
    setLanesOpen(false);
    setMilestone(undefined);
    setDeleteTarget(null);
    setError(null);
    load();
    return () => { loadRequest.current = null; };
  }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  // Use the board's task query convention so opening a row is shareable and
  // browser navigation restores the same modal on the Horizon.
  useEffect(() => {
    let live = true;
    setTask(null);
    if (taskParam) {
      axios.get(`/tasks/${taskParam}`).then(({ data }) => {
        if (!live) return;
        const taskProject = data.project?._id || data.project;
        if (taskProject && String(taskProject) !== String(projectId)) {
          setError('This task does not belong to this project.');
          return;
        }
        setTask(data);
      }).catch(e => { if (live) setError(errorOf(e)); });
    }
    return () => { live = false; };
  }, [taskParam, projectId, horizonId]);

  const previewSchedule = useCallback(h => {
    if (h && String(h._id) === String(horizonId)) setHorizon(h);
  }, [horizonId]);

  // A task finished on the board should show here without a reload, so refetch
  // whenever this tab comes back to the front.
  useEffect(() => {
    const refresh = () => { if (!document.hidden) load(); };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  /** Run a mutation whose answer is the full Horizon. */
  const save = async (promise) => {
    try {
      show((await promise).data);
    } catch (e) {
      setError(errorOf(e));
      throw e;
    }
  };

  const openCreate = () => {
    createTrigger.current = document.activeElement;
    setForm({ name: '', anchor: mondayOf(now), weeks: '26' });
    setFormError(null);
    setCreating(true);
  };

  const closeCreate = () => { if (!createBusy.current) setCreating(false); };

  const deleteHorizon = async target => {
    await axios.delete(`/horizons/${target._id}`);
    if (!mounted.current || activeProjectId.current !== projectId) return;
    const remaining = (list || []).filter(h => h._id !== target._id);
    setList(prev => (prev || []).filter(h => h._id !== target._id));
    setDeleteTarget(null);
    if (activeHorizonId.current !== target._id) return;
    // Ignore reads and saves that started before deletion completed.
    loadRequest.current = null;
    activeHorizonId.current = null;
    setHorizon(null);
    setTask(null);
    setToast(null);
    setError(null);
    navigate(remaining.length
      ? `/project/${projectId}/horizon/${remaining[0]._id}`
      : `/project/${projectId}/horizon`, { replace: true });
  };

  const create = async (event) => {
    event.preventDefault();
    if (createBusy.current) return;
    const name = form.name.trim();
    if (!name) { setFormError('Give the Horizon a name.'); return; }
    const day = parseDay(form.anchor);
    if (!day || day.getUTCDay() !== 1) {
      setFormError('Week 1 must start on a Monday.');
      return;
    }
    const weeks = Number(form.weeks);
    if (!Number.isInteger(weeks) || weeks < 1) {
      setFormError('Weeks must be a positive whole number.');
      return;
    }
    setFormError(null);
    createBusy.current = true;
    setCreatingBusy(true);
    try {
      const res = await axios.post(`/horizons/project/${projectId}`, {
        name,
        anchorDate: form.anchor,
        weeks,
      });
      setList((prev) => [res.data, ...(prev || [])]);
      setCreating(false);
      navigate(`/project/${projectId}/horizon/${res.data._id}`);
    } catch (e) {
      setFormError(errorOf(e));
    } finally {
      createBusy.current = false;
      setCreatingBusy(false);
    }
  };

  // Drag-and-drop: show the client's own schedule at once, then let the
  // server's answer replace it. A refusal puts the old plan back.
  const reorder = async (orderedIds) => {
    if (reordering) return;
    const previous = horizon;
    setReordering(true);
    setHorizon(applyOrder(horizon, orderedIds));
    try {
      const res = await axios.put(`/horizons/${horizon._id}/order`, { orderedIds });
      show(res.data);
    } catch (e) {
      if (String(previous._id) === String(activeHorizonId.current)) {
        setHorizon(previous);
        setError(errorOf(e));
      }
    } finally {
      setReordering(false);
    }
  };

  const openRow = ({ kind, id, row }) => {
    setSelectedId(id);
    if (kind === 'milestone') { setMilestone(row); return; }
    if (row.taskState === 'deleted') return;     // nothing left to open
    const query = new URLSearchParams(location.search);
    query.set('task', row.task);
    query.set('row', id);
    navigate({ pathname: location.pathname, search: query.toString() });
  };

  const closeTask = () => {
    setTask(null);
    const query = new URLSearchParams(location.search);
    query.delete('task');
    navigate({ pathname: location.pathname, search: query.toString() }, { replace: true });
    load();
  };

  const pushDates = async () => {
    try {
      const res = await axios.post(`/horizons/${horizon._id}/push-dates`);
      const { changed, unchanged, skipped } = res.data;
      setToast(`Due dates: ${changed} changed, ${unchanged} unchanged, ${skipped} skipped`);
    } catch (e) {
      setError(errorOf(e));
    }
  };

  const openPicker = async () => {
    try {
      const res = await axios.get(`/tasks/project/${projectId}`);
      const tasks = res.data.tasks || [];
      // The board deliberately caps Done. Horizon can also schedule older
      // unarchived work, so complete that slice through the paged endpoint.
      const byId = new Map(tasks.map(task => [task._id, task]));
      if (res.data.doneTruncated) {
        let skip = 0;
        let hasMore = true;
        while (hasMore) {
          const { data } = await axios.get(`/tasks/project/${projectId}/completed?state=active&limit=100&skip=${skip}`);
          for (const task of data.tasks || []) byId.set(task._id, task);
          skip += (data.tasks || []).length;
          hasMore = data.hasMore && (data.tasks || []).length > 0;
        }
      }
      setPickerTasks([...byId.values()]);
    } catch (e) {
      setError(errorOf(e));
    }
  };

  if (!project || !list) return (
    <div className="hzpage hzpage-loading">
      {error ? <div className="hzpage-error" role="alert">{error} <Link to="/">Back to projects</Link></div> : 'Loading…'}
    </div>
  );

  const members = project.members || [];

  return (
    <>
      <div className="kanban-container hzpage">
        {/* The project header, as on the board — same identity row, a nav row
            in place of the board's own controls. */}
        <div className="kanban-header">
          <div className="kanban-header-identity">
            <Link to="/" className="btn btn-ghost kanban-back"><ArrowLeft size={18} /></Link>
            {project.image ? (
              <div className="project-header-image" style={{ backgroundImage: `url(${mediaUrl(project.image)})` }} />
            ) : (
              <div className="project-color-dot" style={{ background: project.color }} />
            )}
            <div className="kanban-header-titles">
              <h1 title={project.name}>{project.name}</h1>
              {project.description && <p className="project-subtitle" title={project.description}>{project.description}</p>}
            </div>
            {members.length > 0 && (
              <div className="avatar-group">
                {members.slice(0, 3).map((m, i) => (
                  <UserAvatar key={m._id} user={m} className="avatar" title={m.name} style={{ zIndex: members.length - i }} />
                ))}
                {members.length > 3 && <div className="avatar avatar-overflow">+{members.length - 3}</div>}
              </div>
            )}
          </div>

          <div className="kanban-header-toolbar">
            <button className="btn btn-ghost hzpage-nav-board" onClick={() => navigate(`/project/${projectId}`)}>
              <LayoutGrid size={18} />
              Board
            </button>
            <button className="btn btn-ghost hzpage-nav-folios" onClick={() => navigate(`/project/${projectId}/folios`)}>
              <FileText size={18} />
              Folios
            </button>
            <button className="btn btn-ghost hzpage-nav-horizon is-active">
              <CalendarRange size={18} />
              Horizon
            </button>
          </div>
        </div>

        {error && (
          <div className="hzpage-error">
            {error} <Link to={`/project/${projectId}`}>Back to the board</Link>
          </div>
        )}

        {!horizonId && list.length === 0 && (
          <div className="hzpage-empty">
            <p>No Horizons yet.</p>
            <p className="hzpage-empty-hint">A Horizon lays this project&apos;s tasks out over weeks, scheduling them from their durations and dependencies.</p>
            <button className="btn btn-primary hzpage-create" onClick={openCreate}>Create horizon</button>
          </div>
        )}

        {horizon && (
          <>
            <div className="hzpage-toolbar">
              <select
                className="hzpage-switcher"
                aria-label="Horizon"
                value={horizon._id}
                onChange={(e) => {
                  if (e.target.value === '__new__') { openCreate(); return; }
                  navigate(`/project/${projectId}/horizon/${e.target.value}`);
                }}
              >
                {list.map((h) => <option key={h._id} value={h._id}>{h.name}</option>)}
                <option value="__new__">New horizon…</option>
              </select>

              <span className="hzpage-anchor">{formatAnchor(horizon.anchorDate, horizon.weeks)}</span>

              <button className="btn btn-ghost hzpage-delete" onClick={() => setDeleteTarget(horizon)}>
                <Trash2 size={18} /> Delete Horizon
              </button>

              <span className="hzpage-spacer" />

              <span className="hzpage-zoomgroup">
                {['week', 'month'].map((z) => (
                  <button
                    key={z}
                    className={`hzpage-zoom${zoom === z ? ' is-active' : ''}`}
                    data-zoom={z}
                    onClick={() => setZoom(z)}
                  >
                    {z === 'week' ? 'Week' : 'Month'}
                  </button>
                ))}
              </span>

              <button className="btn btn-ghost hzpage-links" onClick={() => setLinks((v) => !v)}>
                <Link2 size={18} />
                Links {links ? 'on' : 'off'}
              </button>
              <button className="btn btn-ghost hzpage-lanes" onClick={() => setLanesOpen(true)}>
                <Layers size={18} />
                Lanes
              </button>
              <button className="btn btn-ghost hzpage-push" onClick={pushDates}>
                <CalendarCheck size={18} />
                Push dates to tasks
              </button>
              <select className="hzpage-paper" aria-label="Print paper size" value={paper}
                onChange={e => setPaper(e.target.value)}>
                <option value="A3">A3 landscape</option>
                <option value="A4">A4 landscape</option>
              </select>
              <button className="btn btn-ghost hzpage-print-button" onClick={() => window.print()}>
                <Printer size={18} /> Print / PDF
              </button>
              <button className="btn btn-ghost hzpage-milestone" onClick={() => setMilestone(null)}>
                <Diamond size={18} />
                Milestone
              </button>
              <button className="btn btn-primary hzpage-add" onClick={openPicker}>
                <Plus size={18} />
                Add tasks
              </button>
            </div>

            {toast && <div className="hzpage-toast">{toast}</div>}

            <div className="hzpage-timeline">
              <HorizonTimeline
                key={horizon._id}
                horizon={horizon}
                zoom={zoom}
                now={now}
                showLinks={links}
                selectedId={selectedId}
                onRowClick={openRow}
                onReorder={reorder}
                onRemove={async id => {
                  if (!window.confirm('Remove this row from the Horizon?')) return;
                  try { await save(axios.delete(`/horizons/${horizon._id}/entries/${id}`)); } catch { /* error is shown by save */ }
                }}
              />
            </div>
          </>
        )}
      </div>

      {printing && horizon && (
        <HorizonPrint horizon={horizon} project={project} paper={paper} now={now} showLinks={links} />
      )}

      {deleteTarget && (
        <HorizonDeleteDialog horizon={deleteTarget} onDelete={deleteHorizon} onClose={closeDelete} />
      )}

      {creating && (
        <div className="modal-overlay" onClick={closeCreate}>
          <form ref={createDialog} className="modal-content hzpage-create-dialog" role="dialog" aria-modal="true"
            aria-labelledby="hzpage-create-title" aria-busy={creatingBusy} noValidate
            onSubmit={create} onClick={(e) => e.stopPropagation()}>
            <div className="hzpage-create-header">
              <div>
                <h2 id="hzpage-create-title"><CalendarRange size={20} /> New horizon</h2>
                <p>Plan your project's work week by week.</p>
              </div>
              <button type="button" className="btn btn-ghost hzpage-create-close" aria-label="Close New horizon"
                disabled={creatingBusy} onClick={closeCreate}><X size={20} /></button>
            </div>
            <div className="hzpage-create-body">
            <label htmlFor="hzpage-create-name">
              Name
              <input
                id="hzpage-create-name" ref={createName} className="hzpage-create-name"
                placeholder="e.g. Prototype build" required disabled={creatingBusy}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
            <label htmlFor="hzpage-create-anchor">
              Week 1 starts
              <input
                type="date"
                id="hzpage-create-anchor" className="hzpage-create-anchor" required disabled={creatingBusy}
                aria-describedby="hzpage-anchor-help"
                value={form.anchor}
                onChange={(e) => setForm({ ...form, anchor: e.target.value })}
              />
              <span id="hzpage-anchor-help" className="hzpage-create-help">Choose a Monday for the first week of your plan.</span>
            </label>
            <label htmlFor="hzpage-create-weeks">
              Weeks
              <input
                type="number"
                min="1"
                step="1" id="hzpage-create-weeks" className="hzpage-create-weeks" required disabled={creatingBusy}
                aria-describedby="hzpage-weeks-help"
                value={form.weeks}
                onChange={(e) => setForm({ ...form, weeks: e.target.value })}
              />
              <span id="hzpage-weeks-help" className="hzpage-create-help">The default planning grid is 26 weeks.</span>
            </label>
            {formError && <div className="hzpage-create-error" role="alert">{formError}</div>}
            </div>
            <div className="hzpage-dialog-actions">
              <button type="button" className="btn btn-secondary" disabled={creatingBusy} onClick={closeCreate}>Cancel</button>
              <button type="submit" className="btn btn-primary hzpage-create-save" disabled={creatingBusy}>
                {creatingBusy ? 'Creating…' : 'Create horizon'}
              </button>
            </div>
          </form>
        </div>
      )}

      {pickerTasks && horizon && (
        <HorizonPicker
          horizon={horizon}
          project={project}
          tasks={pickerTasks}
          onAdd={(ids) => save(axios.post(`/horizons/${horizon._id}/entries`, ids.map((t) => ({ task: t }))))}
          onClose={() => setPickerTasks(null)}
        />
      )}

      {lanesOpen && horizon && (
        <div className="hzpage-popover">
          <HorizonLanes
            horizon={horizon}
            members={members}
            onSave={(lanes) => save(axios.put(`/horizons/${horizon._id}`, { lanes }))}
            onClose={() => setLanesOpen(false)}
          />
        </div>
      )}

      {milestone !== undefined && horizon && (
        <HorizonMilestoneDialog
          horizon={horizon}
          milestone={milestone}
          onSave={(body) => save(milestone
            ? axios.put(`/horizons/${horizon._id}/milestones/${milestone._id}`, body)
            : axios.post(`/horizons/${horizon._id}/milestones`, body))}
          onDelete={() => axios.delete(`/horizons/${horizon._id}/milestones/${milestone._id}`).then(load)}
          onClose={() => setMilestone(undefined)}
        />
      )}

      {task && (
        <TaskModal
          task={task}
          project={project}
          focusSection="horizon"
          horizonId={horizon._id}
          onHorizonScheduleChange={previewSchedule}
          onClose={closeTask}
          onUpdate={async (taskId, data) => (await axios.put(`/tasks/${taskId}`, data)).data}
          onDelete={async (taskId) => { await axios.delete(`/tasks/${taskId}`); closeTask(); }}
          onArchive={async (taskId) => { const r = await axios.put(`/tasks/${taskId}/archive`); closeTask(); return r.data; }}
          onCreateTag={async (name, color) => {
            const res = await axios.post(`/projects/${projectId}/tags`, { name, color });
            setProject((p) => ({ ...p, tags: [...(p.tags || []), res.data] }));
            return res.data;
          }}
        />
      )}
    </>
  );
}

export default HorizonPage;
